/**
 *  Copyright (C) 2022 - Jeci SARL - https://jeci.fr
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU Affero General Public License as
 *  published by the Free Software Foundation, either version 3 of the
 *  License, or (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU Affero General Public License for more details.
 *
 *  You should have received a copy of the GNU Affero General Public License
 *  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import configService from "./ConfigService.js";
import { nodesApi } from "../AlfrescoApi.js";
import { useConfigStore } from "@/stores/config.js";

const PRISTY_CONFIG_NODE_ID = "pristy-config";

class ClassificationService {
  /**
   * Fetches a specific classification plan from the config service.
   * @param {string} filename The name of the plan file (e.g., 'plan-classification.json').
   * @returns {Promise<any>} A promise that resolves with the JSON content of the plan.
   */
  getPlan(filename) {
    const configStore = useConfigStore();
    return configService
      .getConfig(configStore.INSTANCE_NAME, filename)
      .then((response) => response.jsonContent);
  }

  /**
   * Saves a specific classification plan using the config service.
   * @param {string} filename The name of the plan file.
   * @param {object} content The JSON object to save.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   */
  savePlan(filename, content) {
    const configStore = useConfigStore();
    return configService.updateConfig(
      configStore.INSTANCE_NAME,
      filename,
      content,
    );
  }

  /**
   * Lists all existing classification plans.
   * @returns {Promise<string[]>} A promise that resolves with an array of filenames.
   */
  async listPlans() {
    const children = await this.#listInstanceChildren(["properties"]);
    return children
      .filter(
        (entry) =>
          entry.name.startsWith("plan-") && entry.name.endsWith(".json"),
      )
      .map((entry) => entry.name);
  }

  /**
   * Fetches a classification plan along with its allowableOperations.
   * @param {string} filename The name of the plan file.
   * @returns {Promise<{content: any, allowableOperations: string[]}>}
   */
  async getPlanWithPermissions(filename) {
    const [children, content] = await Promise.all([
      this.#listInstanceChildren(["allowableOperations"]),
      this.getPlan(filename),
    ]);
    const entry = children.find((e) => e.name === filename);
    const allowableOperations = entry?.allowableOperations || [];
    return { content, allowableOperations };
  }

  /**
   * Lists all existing classification plan entries (full Alfresco nodes).
   * @returns {Promise<Object[]>} A promise that resolves with an array of Alfresco entry objects.
   */
  async listPlanEntries() {
    const children = await this.#listInstanceChildren([
      "properties",
      "allowableOperations",
    ]);
    return children.filter(
      (entry) => entry.name.startsWith("plan-") && entry.name.endsWith(".json"),
    );
  }

  async #listInstanceChildren(include) {
    const configStore = useConfigStore();
    try {
      const children = await nodesApi.listNodeChildren(PRISTY_CONFIG_NODE_ID, {
        include,
      });
      const instanceFolder = children.list.entries.find(
        (e) => e.entry.name === configStore.INSTANCE_NAME,
      );
      if (!instanceFolder) {
        return [];
      }
      const instanceChildren = await nodesApi.listNodeChildren(
        instanceFolder.entry.id,
        { include },
      );
      return instanceChildren.list.entries.map((entry) => entry.entry);
    } catch {
      return [];
    }
  }
}

export default new ClassificationService();
